// @flow

import * as React from 'react';
import CompareBy from './CompareBy';
import FilterRows from './FilterRows';
import { Tooltip } from '../../core';
import type { Filters } from '../../../../main/types/druidTypes';

/**
 * PureQueryBuilder: A component that provides the User Interface for constructing a Cenarius Query via the Dashboard.
 * DruidQueryBuilder differs from PureQueryBuilder because it is stateful and contains logic that is specific to constructing queries on pages where a query builder must appear stacked above a timeseries or table (e.g. ScreenAdsAnalytics, ScreenEmailAnalytics).
 * Since the User Interface for constructing queries should be independent of where and how it is used, PureQueryBuilder was implemented.
 * The use case was cheifly to place a query builder in a modal (e.g. Cohorts).
 */

type Props = {
  dimensions: Array<string>,
  eventSelect?: React.Element<any>,
  cohortSelect?: React.Element<any>,
  fraudSelect?: React.Element<any>,
  hideCompareBy?: boolean,
  styleVariant?: string,
  dimensionsToCompareBy: Array<string>,
  dimensionsToFilterBy: Array<string>,
  maxDimensions: number,
  minDimensions: number,
  filter?: Object,
  requiredFilter?: Object,
  allFilters: Filters,
  unique?: boolean,
  updateUnique?: () => void,
  disableUniqueToggle?: boolean,
  organic?: boolean,
  updateOrganic?: () => void,
  disableOrganicToggle?: boolean,
  hasExportButton?: boolean,
  hasUniqueToggle?: boolean,
  hasOrganicToggle?: boolean,
  hasFraudDescriptionToggle?: boolean,
  showFraudDescription?: boolean,
  toggleFraudDescription?: Object => void,
  testid: string,
  updateQuery: Object => void,
  title?: string,
  tooltip?: string | React.Element<any>,
  updateFraudTypeOnCompareByChange?: () => void,
  customLabelMap?: Object,
  createFilterValues?: boolean,
  fetchFilters: string => void,
  recordLimit?: number,
  updateGranularity?: Object => void,
  granularity?: string,
  updateBlockedEventIndicator?: string => void,
  context?: string,
  query?: Object
};

class PureQueryBuilder extends React.PureComponent<Props> {
  renderTitle = () => {
    const props = this.props;

    if (!props.title) {
      return null;
    }

    if (props.tooltip) {
      return (
        <div className="analytics__title">
          <Tooltip body={props.tooltip}>{props.title}</Tooltip>
        </div>
      );
    }

    return <div className="analytics__title">{props.title}</div>;
  };

  render() {
    const props = this.props;
    return (
      <div>
        {props.cohortSelect &&
          React.cloneElement(props.cohortSelect, {
            updateQuery: props.updateQuery
          })}
        {this.renderTitle()}
        <div className="analytics-filters">
          {props.fraudSelect &&
            React.cloneElement(props.fraudSelect, {
              dimensions: props.dimensions,
              updateQuery: props.updateQuery
            })}
          <div className="analytics-filters__row">
            {props.eventSelect &&
              React.cloneElement(props.eventSelect, {
                updateQuery: props.updateQuery
              })}
            {!props.hideCompareBy && (
              <CompareBy
                allFilters={props.allFilters}
                dimensions={props.dimensions}
                dimensionsToDisplay={props.dimensionsToCompareBy}
                maxDimensions={props.maxDimensions}
                minDimensions={props.minDimensions}
                updateFraudTypeOnCompareByChange={
                  props.updateFraudTypeOnCompareByChange
                }
                updateQuery={props.updateQuery}
                customLabelMap={props.customLabelMap}
                recordLimit={props.recordLimit}
                context={props.context}
                granularity={props.granularity}
                query={props.query}
                organic={props.organic}
              />
            )}
          </div>
          <FilterRows
            allFilters={props.allFilters}
            requiredFilter={props.requiredFilter}
            filter={props.filter}
            fetchFilters={props.fetchFilters}
            unique={props.unique}
            updateUnique={props.updateUnique}
            disableUniqueToggle={props.disableUniqueToggle}
            organic={props.organic}
            updateOrganic={props.updateOrganic}
            disableOrganicToggle={props.disableOrganicToggle}
            hasFraudDescriptionToggle={props.hasFraudDescriptionToggle}
            showFraudDescription={props.showFraudDescription}
            toggleFraudDescription={props.toggleFraudDescription}
            hasExportButton={props.hasExportButton}
            testid={props.testid}
            hasUniqueToggle={props.hasUniqueToggle}
            hasOrganicToggle={props.hasOrganicToggle}
            dimensionsToDisplay={props.dimensionsToFilterBy}
            updateQuery={props.updateQuery}
            customLabelMap={props.customLabelMap}
            createFilterValues={props.createFilterValues}
            updateGranularity={props.updateGranularity}
            granularity={props.granularity}
            updateBlockedEventIndicator={props.updateBlockedEventIndicator}
            context={props.context}
            dimensions={props.dimensions}
            styleVariant={props.styleVariant}
          />
        </div>
      </div>
    );
  }
}

export default PureQueryBuilder;
